// Scans started without logging in (POST /public/scans). Each one is readable only with the
// secret access token the backend returned when it was created, so the tokens are kept here,
// sent as X-Scan-Token by the API client, and handed to POST /scans/claim after login.
//
// The shareable results link carries the token in the URL fragment (#t=...), which browsers
// never send to a server or put in a Referer header.

// Must match the backend's ENABLE_ANONYMOUS_SCANS: with it off, every scan page needs a login.
export const GUEST_SCANS_ENABLED = process.env.REACT_APP_ENABLE_GUEST_SCANS === 'true';

const STORAGE_KEY = 'kira_guest_scans';
const MAX_SCANS = 20;

const read = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((s) => s && s.scanId && s.token) : [];
  } catch {
    return [];
  }
};

const write = (scans) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(scans.slice(-MAX_SCANS)));
  } catch {
    // Storage full or disabled: the results link still works for this visit.
  }
};

export const listGuestScans = () => read();

export const getGuestToken = (scanId) => {
  const found = read().find((s) => s.scanId === scanId);
  return found ? found.token : null;
};

export const saveGuestScan = ({ scanId, token, repoUrl }) => {
  if (!scanId || !token) return;
  const others = read().filter((s) => s.scanId !== scanId);
  write([...others, { scanId, token, repoUrl: repoUrl || '', createdAt: new Date().toISOString() }]);
};

export const removeGuestScans = (scanIds) => {
  const drop = new Set(scanIds);
  write(read().filter((s) => !drop.has(s.scanId)));
};

export const clearGuestScans = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

// The token from a results link's fragment ("#t=<token>"), if there is one.
export const tokenFromHash = (hash) => {
  const params = new URLSearchParams((hash || '').replace(/^#/, ''));
  return params.get('t');
};

export const guestResultsPath = (scanId, token) =>
  `/results/${encodeURIComponent(scanId)}${token ? `#t=${encodeURIComponent(token)}` : ''}`;

// Where a guest was when they chose to sign in, so login can bring them back to the scan
// they were looking at, now theirs. Only a results path is kept (never the token: once the
// scan is claimed the account reads it), and only for an hour, so a stale value can neither
// redirect anywhere else nor hijack a later, unrelated login.
const RETURN_KEY = 'kira_return_to';
const RETURN_TTL_MS = 60 * 60 * 1000;
const RETURN_PATH = /^\/results\/[^/?#]+$/;

export const saveReturnPath = (path) => {
  if (!RETURN_PATH.test(path || '')) return;
  try {
    window.localStorage.setItem(RETURN_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Storage disabled: login lands on the default page instead.
  }
};

const readReturnPath = () => {
  try {
    const { path, at } = JSON.parse(window.localStorage.getItem(RETURN_KEY) || 'null') || {};
    return RETURN_PATH.test(path || '') && Date.now() - at < RETURN_TTL_MS ? path : null;
  } catch {
    return null;
  }
};

export const hasReturnPath = () => readReturnPath() !== null;

// The saved path, if still valid, removed so it is used once.
export const takeReturnPath = () => {
  const path = readReturnPath();
  try {
    window.localStorage.removeItem(RETURN_KEY);
  } catch {
    // ignore
  }
  return path;
};
